/**
 * A photo's edits, as a DESCRIPTION applied to the untouched original — never
 * as changed pixels (design round of 2026-09-29, `CLAUDE-CODE-photo-editor.md`).
 *
 *     { crop, rot, straighten, brightness, contrast, bg, bgFill, strokes }
 *
 * 🔴 WHY A DESCRIPTION. Edits AUTO-SAVE — there is no Save button, because a
 * worker forgets to press one. Saving pixels on every change would destroy the
 * original a step at a time. Saving a description never does: Undo, "Hold to
 * compare" and Reset always have the original to go back to, on this tablet or
 * any other, and the photo the bidders see is a RENDER of the description,
 * made again whenever it changes.
 *
 * Every rule about what a description may hold and how it maps to pixels lives
 * here, once. The cataloging app draws with it; the `save-photo-edit` Edge
 * Function validates with a copy that its tests hold equal to this one (Deno
 * cannot import a workspace package).
 *
 * Pure — no React, no Skia, no network.
 */

export type CropPreset = 'original' | 'square' | '4:3' | '3:4';
export type QuarterTurn = 0 | 90 | 180 | 270;
/** What fills the space the background left: white, or nothing (transparent). */
export type BackgroundFill = 'white' | 'none';

/** A touch-up brush: take part of the item away, or bring back part of the photo. */
export type BrushMode = 'erase' | 'restore';

/**
 * One finger stroke over a cut-out (Jim, 2026-10-05: "we need a way to
 * manually erase after background removal … use the finger to erase on the
 * screen").
 *
 * 🔴 IN THE ORIGINAL'S PIXELS — the space the cut-out mask is in. A stroke
 * drawn on a rotated, straightened, cropped view is stored where it fell on
 * the photo itself, so turning or cropping afterwards leaves it on the item.
 * It changes the MASK, never the photo: erase makes the mask clear under it,
 * restore makes it solid. The phone's own mask stays untouched beside it, so
 * Undo and Reset always have it to go back to.
 */
export interface BrushStroke {
  mode: BrushMode;
  /** The brush's diameter, in the original's pixels. */
  size: number;
  /** x0, y0, x1, y1, … — whole pixels of the original. One point is a dab. */
  points: number[];
}

export interface PhotoEdit {
  crop: CropPreset;
  /** Clockwise quarter turns. */
  rot: QuarterTurn;
  /** Degrees, clockwise positive, on top of `rot`. */
  straighten: number;
  /** −50…50; 0 leaves the photo as it was. */
  brightness: number;
  /** −50…50; 0 leaves the photo as it was. */
  contrast: number;
  /** The background has been removed (the cut-out mask is stored beside the photo). */
  bg: boolean;
  bgFill: BackgroundFill;
  /** Touch-ups to the cut-out, in order. Meaningless — and cleared — while `bg` is false. */
  strokes: BrushStroke[];
}

export const PHOTO_EDIT_NONE: Readonly<PhotoEdit> = Object.freeze({
  crop: 'original',
  rot: 0,
  straighten: 0,
  brightness: 0,
  contrast: 0,
  bg: false,
  bgFill: 'white',
  strokes: Object.freeze([]) as unknown as BrushStroke[],
});

export const CROP_PRESETS: readonly { key: CropPreset; label: string }[] = [
  { key: 'original', label: 'Original' },
  { key: 'square', label: 'Square' },
  { key: '4:3', label: '4:3' },
  { key: '3:4', label: '3:4' },
];

/** Slider ranges. The editor's sliders and the server's validation both read these. */
export const PHOTO_EDIT_LIMITS = Object.freeze({
  straighten: { min: -45, max: 45, step: 0.5 },
  brightness: { min: -50, max: 50, step: 1 },
  contrast: { min: -50, max: 50, step: 1 },
});

/**
 * How much touch-up one photo may carry. Generous for a worker — a stroke is
 * one finger-down to finger-up — and a ceiling on what a description can
 * weigh: at the limits it is a few hundred KB of JSON, typically under one.
 */
export const BRUSH_LIMITS = Object.freeze({
  maxStrokes: 200,
  /** Points in one stroke. The editor starts a new stroke past this. */
  maxPoints: 1000,
  /** Points across every stroke on the photo. */
  maxTotalPoints: 20000,
  /** Brush diameter, original pixels. */
  size: Object.freeze({ min: 1, max: 4000 }),
  /** Largest coordinate. No camera this app supports makes a photo this big. */
  maxCoord: 20000,
});

const CROPS = new Set<string>(CROP_PRESETS.map((c) => c.key));
const TURNS = new Set<number>([0, 90, 180, 270]);
const FILLS = new Set<string>(['white', 'none']);

/** Snap a slider value to its step and range. `-0` comes out as `0`. */
export function snapSlider(key: keyof typeof PHOTO_EDIT_LIMITS, value: number): number {
  const { min, max, step } = PHOTO_EDIT_LIMITS[key];
  const clamped = Math.min(max, Math.max(min, value));
  const snapped = Math.round(clamped / step) * step;
  // Floating-point tidy-up: 0.5 steps must read back as 12.5, not 12.499999.
  return Number(snapped.toFixed(1)) + 0;
}

function isSliderValue(key: keyof typeof PHOTO_EDIT_LIMITS, v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v) && snapSlider(key, v) === v;
}

/** Touch-up strokes read from storage, or `null` if any of them is not one. Absent is none. */
function parseStrokes(raw: unknown): BrushStroke[] | null {
  if (raw === undefined) return [];
  if (!Array.isArray(raw) || raw.length > BRUSH_LIMITS.maxStrokes) return null;
  const out: BrushStroke[] = [];
  let total = 0;
  for (const s of raw as unknown[]) {
    if (typeof s !== 'object' || s === null || Array.isArray(s)) return null;
    const { mode, size, points } = s as Record<string, unknown>;
    if (mode !== 'erase' && mode !== 'restore') return null;
    if (
      typeof size !== 'number' ||
      !Number.isInteger(size) ||
      size < BRUSH_LIMITS.size.min ||
      size > BRUSH_LIMITS.size.max
    ) {
      return null;
    }
    if (
      !Array.isArray(points) ||
      points.length < 2 ||
      points.length % 2 !== 0 ||
      points.length / 2 > BRUSH_LIMITS.maxPoints
    ) {
      return null;
    }
    for (const c of points as unknown[]) {
      if (typeof c !== 'number' || !Number.isInteger(c) || c < 0 || c > BRUSH_LIMITS.maxCoord) {
        return null;
      }
    }
    total += points.length / 2;
    if (total > BRUSH_LIMITS.maxTotalPoints) return null;
    out.push({ mode, size, points: [...(points as number[])] });
  }
  return out;
}

/**
 * A description read from storage, or `null` if it is not one.
 *
 * STRICT: an out-of-range or unknown value is not clamped into something the
 * worker never chose — the whole description is refused. Missing keys take
 * their no-edit value, so a description written before a key existed still
 * reads. Extra keys are ignored.
 */
export function parsePhotoEdit(raw: unknown): PhotoEdit | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const e: PhotoEdit = {
    crop: (r.crop ?? PHOTO_EDIT_NONE.crop) as CropPreset,
    rot: (r.rot ?? PHOTO_EDIT_NONE.rot) as QuarterTurn,
    straighten: (r.straighten ?? 0) as number,
    brightness: (r.brightness ?? 0) as number,
    contrast: (r.contrast ?? 0) as number,
    bg: (r.bg ?? false) as boolean,
    bgFill: (r.bgFill ?? PHOTO_EDIT_NONE.bgFill) as BackgroundFill,
    strokes: [],
  };
  const strokes = parseStrokes(r.strokes);
  if (strokes === null) return null;
  e.strokes = strokes;
  if (!CROPS.has(e.crop)) return null;
  if (!TURNS.has(e.rot)) return null;
  if (!isSliderValue('straighten', e.straighten)) return null;
  if (!isSliderValue('brightness', e.brightness)) return null;
  if (!isSliderValue('contrast', e.contrast)) return null;
  if (typeof e.bg !== 'boolean') return null;
  if (!FILLS.has(e.bgFill)) return null;
  return e;
}

export function samePhotoEdit(a: PhotoEdit, b: PhotoEdit): boolean {
  return (
    a.crop === b.crop &&
    a.rot === b.rot &&
    a.straighten === b.straighten &&
    a.brightness === b.brightness &&
    a.contrast === b.contrast &&
    a.bg === b.bg &&
    // The fill and the touch-ups mean nothing while the background is still there.
    (!a.bg || (a.bgFill === b.bgFill && sameStrokes(a.strokes, b.strokes)))
  );
}

export function sameStrokes(a: readonly BrushStroke[], b: readonly BrushStroke[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((s, i) => {
    const t = b[i]!;
    return (
      s.mode === t.mode &&
      s.size === t.size &&
      s.points.length === t.points.length &&
      s.points.every((c, j) => c === t.points[j])
    );
  });
}

/** Points across every stroke — what `BRUSH_LIMITS.maxTotalPoints` counts. */
export function strokePointCount(strokes: readonly BrushStroke[]): number {
  return strokes.reduce((n, s) => n + s.points.length / 2, 0);
}

/** Whether this description leaves the photo exactly as it was taken. */
export function isNoEdit(e: PhotoEdit): boolean {
  return samePhotoEdit(e, PHOTO_EDIT_NONE);
}

// ─── Geometry ───────────────────────────────────────────────────────────────

export interface Size {
  w: number;
  h: number;
}
export interface Rect extends Size {
  x: number;
  y: number;
}

/**
 * Where everything sits, in the ORIGINAL's pixel units.
 *
 *   frame — the whole photo after the quarter turns (a portrait shot turned
 *           once is landscape). Straightening does not change it.
 *   crop  — the part that is kept: the largest rectangle of the preset's
 *           shape that fits the frame, centred. "Dragging the frame and
 *           pinch-to-zoom aren't designed yet" (README §6).
 *   turn  — the total rotation drawn, quarter turns plus straighten, degrees.
 *   scale — how much the original is enlarged so that, turned by `turn`, it
 *           still COVERS the whole frame: no empty corners, ever (README §6,
 *           Rotate). 1 when nothing is straightened.
 *
 * To draw: translate to the frame's centre, rotate by `turn`, scale by
 * `scale`, draw the original centred on that point; then keep `crop`.
 */
export interface EditGeometry {
  frame: Size;
  crop: Rect;
  turn: number;
  scale: number;
}

export function editGeometry(e: PhotoEdit, original: Size): EditGeometry {
  const frame =
    e.rot % 180 === 0 ? { w: original.w, h: original.h } : { w: original.h, h: original.w };
  const turn = e.rot + e.straighten;
  const r = (turn * Math.PI) / 180;
  const c = Math.abs(Math.cos(r));
  const s = Math.abs(Math.sin(r));
  // A w×h rectangle turned by θ covers a W×H one on the same centre when
  // w ≥ W|cosθ| + H|sinθ| and h ≥ W|sinθ| + H|cosθ|.
  const scale = Math.max(
    (frame.w * c + frame.h * s) / original.w,
    (frame.w * s + frame.h * c) / original.h,
  );
  return { frame, crop: cropRect(e.crop, frame), turn, scale: roundNoise(scale) };
}

/** The preset's shape, width over height; `original` keeps the frame's own. */
export function cropAspect(crop: CropPreset, frame: Size): number {
  switch (crop) {
    case 'original':
      return frame.w / frame.h;
    case 'square':
      return 1;
    case '4:3':
      return 4 / 3;
    case '3:4':
      return 3 / 4;
  }
}

function cropRect(crop: CropPreset, frame: Size): Rect {
  const aspect = cropAspect(crop, frame);
  const frameAspect = frame.w / frame.h;
  const w = aspect > frameAspect ? frame.w : frame.h * aspect;
  const h = aspect > frameAspect ? frame.w / aspect : frame.h;
  return { x: (frame.w - w) / 2, y: (frame.h - h) / 2, w, h };
}

/** A point, in original-pixel units. */
export interface Point {
  x: number;
  y: number;
}

/**
 * A point on the photo as drawn (`view`, in the original's pixel units — what
 * `drawEdit` draws before it is fitted to the screen) → the same spot on the
 * ORIGINAL. Where a touch-up finger lands is stored here (`BrushStroke`).
 *
 * The inverse of `originalToView`: `drawEdit` turns the original about the
 * frame's centre, enlarges it by `scale`, and keeps `crop`.
 */
export function viewToOriginal(
  e: PhotoEdit,
  original: Size,
  p: Point,
  view: 'crop' | 'frame' = 'crop',
): Point {
  const g = editGeometry(e, original);
  const fx = p.x + (view === 'crop' ? g.crop.x : 0) - g.frame.w / 2;
  const fy = p.y + (view === 'crop' ? g.crop.y : 0) - g.frame.h / 2;
  const r = (-g.turn * Math.PI) / 180;
  const c = Math.cos(r);
  const s = Math.sin(r);
  return {
    x: roundNoise((fx * c - fy * s) / g.scale + original.w / 2),
    y: roundNoise((fx * s + fy * c) / g.scale + original.h / 2),
  };
}

/** A spot on the original → where `drawEdit` draws it in `view`. */
export function originalToView(
  e: PhotoEdit,
  original: Size,
  p: Point,
  view: 'crop' | 'frame' = 'crop',
): Point {
  const g = editGeometry(e, original);
  const ox = (p.x - original.w / 2) * g.scale;
  const oy = (p.y - original.h / 2) * g.scale;
  const r = (g.turn * Math.PI) / 180;
  const c = Math.cos(r);
  const s = Math.sin(r);
  return {
    x: roundNoise(ox * c - oy * s + g.frame.w / 2 - (view === 'crop' ? g.crop.x : 0)),
    y: roundNoise(ox * s + oy * c + g.frame.h / 2 - (view === 'crop' ? g.crop.y : 0)),
  };
}

/** cos(90°) is 6e-17, not 0; a scale of 1.0000000000000002 is 1. */
function roundNoise(n: number): number {
  return Math.round(n * 1e9) / 1e9;
}

/**
 * Fit `content` into `box`: `contain` shows all of it (the editor), `cover`
 * fills the box and trims the overflow (a thumbnail). Returns the drawn size
 * and the factor from content units to box units.
 */
export function fitSize(content: Size, box: Size, mode: 'contain' | 'cover'): Size & { k: number } {
  const pick = mode === 'contain' ? Math.min : Math.max;
  const k = pick(box.w / content.w, box.h / content.h);
  return { w: content.w * k, h: content.h * k, k };
}

/** The finished photo's pixel size: the crop, never larger than the original's longest edge. */
export function renderSize(e: PhotoEdit, original: Size): Size {
  const { crop } = editGeometry(e, original);
  return { w: Math.max(1, Math.round(crop.w)), h: Math.max(1, Math.round(crop.h)) };
}

// ─── Colour ─────────────────────────────────────────────────────────────────

/**
 * Brightness then contrast, as a 4×5 colour matrix (row-major, the fifth
 * column an offset in 0…1) — the form Skia takes.
 *
 * The maths is CSS's `brightness(1 + b/100) contrast(1 + c/100)`, which is what
 * the design render shows, so the editor matches what was approved:
 *
 *     v′ = v·B                      brightness
 *     v″ = (v′ − ½)·C + ½           contrast, about mid-grey
 *        = v·B·C + ½(1 − C)
 *
 * Alpha is untouched, so a removed background stays removed.
 */
export function editColorMatrix(e: Pick<PhotoEdit, 'brightness' | 'contrast'>): number[] {
  const B = 1 + e.brightness / 100;
  const C = 1 + e.contrast / 100;
  const g = B * C;
  const o = 0.5 * (1 - C);
  return [g, 0, 0, 0, o, 0, g, 0, 0, o, 0, 0, g, 0, o, 0, 0, 0, 1, 0];
}

/** Whether the colour step changes anything — the drawing can skip it when not. */
export function hasColorChange(e: Pick<PhotoEdit, 'brightness' | 'contrast'>): boolean {
  return e.brightness !== 0 || e.contrast !== 0;
}

/**
 * The format the finished photo is stored in. A transparent background needs
 * an alpha channel, which JPEG does not have; everything else stays JPEG,
 * about a fifth of the size over warehouse wifi.
 */
export function renderFormat(e: PhotoEdit): 'png' | 'jpeg' {
  return e.bg && e.bgFill === 'none' ? 'png' : 'jpeg';
}
